import { useEffect, useState } from "react";
import { EmptyState, SegmentedControl, Skeleton, useToast } from "@astrum/ui";
import type { CapacityResponse } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { hoursLabel } from "../lib/budget-format.js";
import "./capacity-panel.css";

/**
 * The Insights workload in hours: each person against their weekly capacity
 * for the next few weeks. Allocation is computed by the server from open task
 * estimates (spread start to due, shared between co-owners); a week over 100%
 * is highlighted.
 */

const PAGE = 20;

export function CapacityPanel({ projectId }: { projectId?: string }) {
  const { toast } = useToast();
  const [weeks, setWeeks] = useState("4");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<CapacityResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setOffset(0);
  }, [projectId, weeks]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams({ weeks, limit: String(PAGE), offset: String(offset) });
    if (projectId) qs.set("projectId", projectId);
    api
      .get<CapacityResponse>(`/insights/capacity?${qs}`)
      .then((res) => !cancelled && setData(res))
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load capacity.", { tone: "danger" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, weeks, offset, toast]);

  const weekLabel = (key: string) => new Date(`${key}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const unassigned = data ? data.unassignedMinutes.reduce((a, b) => a + b, 0) : 0;

  return (
    <section className="insights-section capacity" aria-label="Capacity">
      <div className="capacity__head">
        <div>
          <h2>Capacity</h2>
          <p className="insights-chart-card__sub">
            Estimated hours per person per week against weekly capacity
            {data && unassigned > 0 ? ` · ${hoursLabel(unassigned)} unassigned` : ""}
            {data && data.unscheduledMinutes > 0 ? ` · ${hoursLabel(data.unscheduledMinutes)} with no due date` : ""}
          </p>
        </div>
        <SegmentedControl
          value={weeks}
          onChange={setWeeks}
          options={[
            { value: "2", label: "2 wk" },
            { value: "4", label: "4 wk" },
            { value: "8", label: "8 wk" },
          ]}
          aria-label="Weeks to show"
        />
      </div>

      {loading && !data ? (
        <Skeleton height={160} />
      ) : !data || data.people.length === 0 ? (
        <EmptyState icon="people" title="No one to plan" description="Add estimates and due dates to tasks to see capacity." />
      ) : (
        <div className="insights-chart-card insights-chart-card--wide capacity__scroll">
          <table className="capacity__table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Capacity</th>
                {data.weeks.map((w) => (
                  <th key={w}>{weekLabel(w)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) => (
                <tr key={p.userId}>
                  <td>{p.displayName ?? "Unknown"}</td>
                  <td>{hoursLabel(p.capacityMinutes)}</td>
                  {p.weeks.map((w) => (
                    <td key={w.weekStart} className={w.over ? "capacity__cell capacity__cell--over" : "capacity__cell"}>
                      <span title={`${w.percent}% of capacity`}>{hoursLabel(w.allocatedMinutes)}</span>
                      <span className="capacity__pct">{w.allocatedMinutes === 0 ? "" : `${w.percent}%`}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.peopleTotal > PAGE && (
            <div className="capacity__pager">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                Previous
              </button>
              <span>
                {offset + 1}-{Math.min(offset + PAGE, data.peopleTotal)} of {data.peopleTotal}
              </span>
              <button type="button" disabled={offset + PAGE >= data.peopleTotal} onClick={() => setOffset(offset + PAGE)}>
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
